import { v4 as uuid } from 'uuid';
import { abortReason, throwIfAborted } from '../abortSignal';
import { QueueWriteCommittedError, validateQueueRecordInput } from './storage';
import { sendFeedbackRecord } from './sender';
import { acceptedIdentity, applySubmissionIdentity } from './identity';
import { withExistingQueueSession } from './ownership';
import { saveQueueSnapshot } from './sessionStorage';

/** One instance per answering session, owned by the caller's Flow lifecycle. */
export function createFeedbackSubmissionSession(submissionId = uuid(), initial = {}) {
  let isProcessing = false;
  let pendingSubmission = null;
  let latestIncompleteRevision = initial.revision || 0;
  let revision = initial.revision || 0;
  let identity = { feedbackId: initial.feedbackId, version: initial.version };
  let usesQueue = initial.usesQueue || false;
  let lastAnswers = initial.responses ? JSON.stringify(initial.responses) : null;
  let hasFinalSubmission = initial.finalized || false;
  let activeJob = null;
  const submissions = new Map();

  async function submitSnapshot(input, { apiUrl, isOnline, getCaptchaToken }, job) {
    const { signal } = job.controller;

    validateQueueRecordInput(input);

    if (input.apiUrl !== apiUrl) throw new TypeError('Submission API environment does not match');

    throwIfAborted(signal);

    const previous = { identity, usesQueue };
    const prepared = applySubmissionIdentity(input, identity);
    identity = { feedbackId: prepared.feedbackId, version: prepared.version };

    if (!isOnline) {
      usesQueue = true;

      try {
        const result = await saveQueueSnapshot(prepared);
        if (result.status === 'queued') identity = { feedbackId: result.feedbackId, version: result.version };

        return result;
      } catch (error) {
        // An aborted write left nothing to send, so the session is as before.
        if (!(error instanceof QueueWriteCommittedError)) {
          ({ identity, usesQueue } = previous);
          job.unsaved = true;
        }

        throw error;
      }
    }

    // Until its POST starts, an online job can still yield to a final submission.
    job.preparing = true;

    const send = async (record, context) => {
      identity = { feedbackId: record.feedbackId, version: record.version };
      const attemptSignal = context?.signal || signal;

      const captchaToken = record.captcha.required
        ? await getCaptchaToken?.(record.captcha, { signal: attemptSignal })
        : undefined;

      throwIfAborted(attemptSignal);
      job.preparing = false;

      const result = await sendFeedbackRecord(record, {
        apiUrl,
        signal: attemptSignal,
        captchaToken,
        beginPost: context?.beginPost,
      });

      if (result.status !== 'submitted') return result;

      identity = acceptedIdentity(record, result.body);

      if (context) {
        try {
          await context.acknowledge(result.body);
        } catch (error) {
          result.cleanupErrors.push({ operation: 'acknowledge', name: error.name });
        }
      }

      return result;
    };

    return usesQueue ? withExistingQueueSession(prepared, send, { signal }) : send(prepared);
  }

  async function processPendingSubmissions() {
    if (isProcessing) return;

    isProcessing = true;

    while (pendingSubmission) {
      const current = pendingSubmission;
      const { signal } = current.options;
      const job = {
        kind: current.input.kind,
        controller: new AbortController(),
        preparing: false,
        superseded: false,
        unsaved: false,
      };
      const abort = () => job.controller.abort(abortReason(signal));
      pendingSubmission = null;
      activeJob = job;
      signal?.addEventListener('abort', abort, { once: true });
      if (signal?.aborted) abort();

      try {
        current.resolve(await submitSnapshot(current.input, current.options, job));
      } catch (error) {
        if (job.superseded) current.resolve({ status: 'skipped', reason: 'superseded' });
        else {
          // Nothing retries a failed final automatically, but the user may submit
          // it again unless an offline save may have left a record to be sent.
          if (current.input.kind === 'final' && (current.options.isOnline || job.unsaved)) {
            submissions.delete(current.key);
            hasFinalSubmission = false;
          }

          current.reject(error);
        }
      } finally {
        signal?.removeEventListener('abort', abort);
        activeJob = null;
      }
    }

    isProcessing = false;
  }

  return {
    submissionId,

    nextRevision() {
      revision += 1;

      return revision;
    },

    getState() {
      return { submissionId, ...identity, revision, usesQueue, finalized: hasFinalSubmission };
    },

    async submit(input, options = {}) {
      if (input.submissionId !== submissionId) throw new TypeError('Submission session does not match');

      // Capture the route before waiting behind other work in this session.
      const isOnline = options.isOnline ?? globalThis.navigator?.onLine !== false;
      const key = JSON.stringify([input.apiUrl, input.kind, input.kind === 'final' ? null : input.revision]);

      if (submissions.has(key)) return submissions.get(key);

      if (hasFinalSubmission) return { status: 'skipped', reason: 'finalized' };

      if (input.kind === 'incomplete') {
        if (input.revision <= latestIncompleteRevision) return { status: 'skipped', reason: 'obsolete' };
        if (JSON.stringify(input.payload.responses) === lastAnswers) return { status: 'skipped', reason: 'unchanged' };
      }

      const snapshot = structuredClone(input);
      const capturedOptions = { ...options, isOnline };

      if (input.kind === 'final') {
        hasFinalSubmission = true;

        // No POST has started, so the final can replace incomplete preparation.
        if (activeJob?.kind === 'incomplete' && activeJob.preparing) {
          activeJob.superseded = true;
          activeJob.controller.abort(new DOMException('Superseded by final submission', 'AbortError'));
        }
      } else {
        latestIncompleteRevision = input.revision;
        lastAnswers = JSON.stringify(input.payload.responses);
      }
      revision = Math.max(revision, input.revision);

      // Only the latest waiting snapshot is useful; never replace the active POST.
      pendingSubmission?.resolve({ status: 'skipped', reason: 'superseded' });

      const pending = new Promise((resolve, reject) => {
        pendingSubmission = { key, input: snapshot, options: capturedOptions, resolve, reject };
      });

      // Callbacks share this attempt; only a failed online final is released.
      submissions.set(key, pending);
      processPendingSubmissions();

      return pending;
    },
  };
}
